import { __assign, __makeTemplateObject, __read, __rest } from "tslib";
import React, { forwardRef, memo, useContext, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { ArrowRight } from '@ringcentral/juno-icon';
import { a11yKeyboard, combineClasses, styled, useEventCallback, useForkRef, useId, useRcPortalWindowContext, useThemeProps, isTap, } from '../../../foundation';
import { ClickAwayListener } from '../../ClickAwayListener';
import { RcIcon } from '../../Icon';
import { RcListItemText } from '../../List/ListItemText';
import { RcPaper } from '../../Paper';
import { RcPopper } from '../../Popper';
import { RcMenuContext } from '../Menu/MenuContext';
import { RcMenuItem } from '../MenuItem/MenuItem';
import { RcMenuList } from '../MenuList/MenuList';
import { RcMenuListContext } from '../MenuList/MenuListContext';
import { StyledGrow, StyledListItemSecondaryAction, SubMenuStyle, } from './styles';
import { RcSubMenuContext } from './SubMenuContext';
import { RcSubMenuClasses } from './utils';
var POPPER_OFFSET = 2;
var MemoMenuList = memo(RcMenuList);
var _RcSubMenu = forwardRef(function (inProps, ref) {
    var props = useThemeProps({ props: inProps, name: 'RcSubMenu' });
    var classesProp = props.classes, children = props.children, titleProp = props.title, disabled = props.disabled, onKeyDown = props.onKeyDown, onTouchStart = props.onTouchStart, onTouchEnd = props.onTouchEnd, onMouseEnter = props.onMouseEnter, onMouseLeave = props.onMouseLeave, MenuListProps = props.MenuListProps, _a = props.PopperProps, PopperProps = _a === void 0 ? {} : _a, onClose = props.onClose, rest = __rest(props, ["classes", "children", "title", "disabled", "onKeyDown", "onTouchStart", "onTouchEnd", "onMouseEnter", "onMouseLeave", "MenuListProps", "PopperProps", "onClose"]);
    var _popperRef = useRef(null);
    var popperRef = useForkRef(_popperRef, PopperProps.ref || null);
    var popperId = useId(PopperProps.id);
    var menuItemIdRef = useRef(null);
    var _b = __read(useState(null), 2), anchorEl = _b[0], setAnchorEl = _b[1];
    var _c = __read(useState(false), 2), open = _c[0], setOpen = _c[1];
    var menuListContext = useContext(RcMenuListContext);
    var menuContext = useContext(RcMenuContext);
    var subMenuContext = useContext(RcSubMenuContext);
    var ignoreMouseEventRef = useRef(false);
    var externalWindow = useRcPortalWindowContext().externalWindow;
    var handleClose = function (e, reason) {
        var _a;
        onClose === null || onClose === void 0 ? void 0 : onClose(e, reason);
        (_a = menuListContext === null || menuListContext === void 0 ? void 0 : menuListContext.onClose) === null || _a === void 0 ? void 0 : _a.call(menuListContext, e, reason);
    };
    var handleItemKeyDown = function (e) {
        var key = e.key;
        var ArrowRight = a11yKeyboard.ArrowRight, Space = a11yKeyboard.Space, Enter = a11yKeyboard.Enter;
        if ([ArrowRight, Space, Enter].includes(key)) {
            openPopper(e);
        }
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
    };
    var handleItemTouchStart = function (e) {
        ignoreMouseEventRef.current = true;
        onTouchStart === null || onTouchStart === void 0 ? void 0 : onTouchStart(e);
    };
    var handleItemTouchEnd = function (e) {
        if (isTap(e)) {
            openPopper(e);
        }
        onTouchEnd === null || onTouchEnd === void 0 ? void 0 : onTouchEnd(e);
    };
    var handleItemMouseEnter = function (e) {
        if (!ignoreMouseEventRef.current) {
            openPopper(e);
        }
        else {
            ignoreMouseEventRef.current = false;
        }
        onMouseEnter === null || onMouseEnter === void 0 ? void 0 : onMouseEnter(e);
    };
    var handleItemMouseLeave = function (e) {
        onMouseLeave === null || onMouseLeave === void 0 ? void 0 : onMouseLeave(e);
        if (!_popperRef.current || !e.currentTarget) {
            return;
        }
        var clientX = e.clientX;
        var _a = _popperRef.current.getBoundingClientRect(), left = _a.left, width = _a.width;
        var isMoveToPopper = left < clientX + POPPER_OFFSET &&
            clientX - POPPER_OFFSET < left + width;
        if (!isMoveToPopper) {
            closePopper();
            handleClose(e, 'subMenuItemAnchorMouseLeave');
        }
    };
    var classes = useMemo(function () { return combineClasses(RcSubMenuClasses, classesProp); }, [classesProp]);
    var title = useMemo(function () {
        return typeof titleProp === 'string' ? (React.createElement(RcListItemText, null, titleProp)) : (titleProp);
    }, [titleProp]);
    var onPopperKeyDown = PopperProps.onKeyDown, onPopperMouseLeave = PopperProps.onMouseLeave, restPopperProps = __rest(PopperProps, ["onKeyDown", "onMouseLeave"]);
    var openPopper = function (event) {
        if (!disabled && event.currentTarget) {
            setAnchorEl(event.currentTarget);
            setOpen(true);
        }
    };
    var closePopper = function () {
        setOpen(false);
        setAnchorEl(null);
    };
    var handleCloseSubMenu = useEventCallback(function () {
        anchorEl === null || anchorEl === void 0 ? void 0 : anchorEl.focus();
        closePopper();
    });
    var handlePopperKeyDown = function (e) {
        var _a;
        var key = e.key;
        var ArrowLeft = a11yKeyboard.ArrowLeft, Escape = a11yKeyboard.Escape, Tab = a11yKeyboard.Tab;
        e.stopPropagation();
        if (key === ArrowLeft) {
            handleCloseSubMenu();
            handleClose(e, 'subMenuItemArrowLeftKeyDown');
        }
        else if ([Escape, Tab].includes(key)) {
            var reason = key === Escape ? 'escapeKeyDown' : 'tabKeyDown';
            handleCloseSubMenu();
            menuContext.closeMenu(e, reason);
            (_a = subMenuContext === null || subMenuContext === void 0 ? void 0 : subMenuContext.closeSubMenu) === null || _a === void 0 ? void 0 : _a.call(subMenuContext, e, reason);
            handleClose(e, reason);
        }
        onPopperKeyDown === null || onPopperKeyDown === void 0 ? void 0 : onPopperKeyDown(e);
    };
    var handlePopperClickAway = function (e) {
        if (anchorEl && e.target && !anchorEl.contains(e.target)) {
            closePopper();
            handleClose(e, 'backdropClick');
        }
    };
    var handlePopperMouseLeave = function (e) {
        closePopper();
        onPopperMouseLeave === null || onPopperMouseLeave === void 0 ? void 0 : onPopperMouseLeave(e);
        if (!anchorEl || !e.currentTarget) {
            return;
        }
        var clientX = e.clientX;
        var _a = anchorEl.getBoundingClientRect(), left = _a.left, width = _a.width;
        var isMoveToAnchor = left < clientX + POPPER_OFFSET &&
            clientX - POPPER_OFFSET < left + width;
        if (!isMoveToAnchor) {
            handleClose(e, 'popperMouseLeave');
        }
    };
    useLayoutEffect(function () {
        if (open &&
            menuListContext.menuListId !== '' &&
            menuListContext.focusedMenuItemId !== menuItemIdRef.current) {
            /** 1. keyboard open the sub-menu
             *  2. mouse hover the sub-menu next item
             *  3. should close the sub-menu popper
             */
            handleCloseSubMenu();
        }
    }, [
        handleCloseSubMenu,
        menuListContext.focusedMenuItemId,
        menuListContext.menuListId,
        open,
    ]);
    useLayoutEffect(function () {
        if (open &&
            menuListContext.menuListId === '' &&
            menuContext.focusedMenuItemId !== menuItemIdRef.current) {
            /**
             * 1. keyboard open the menu inner sub menu
             * 2. mouse hover the sub-menu next item
             * 3. should close all sub-menu popper
             */
            handleCloseSubMenu();
        }
    }, [
        handleCloseSubMenu,
        menuContext.focusedMenuItemId,
        menuListContext.menuListId,
        open,
    ]);
    var ctxValue = useMemo(function () { return ({
        autoClose: menuListContext.autoClose,
        closeSubMenu: function (e, reason) {
            var _a, _b;
            handleCloseSubMenu();
            (_a = subMenuContext === null || subMenuContext === void 0 ? void 0 : subMenuContext.closeSubMenu) === null || _a === void 0 ? void 0 : _a.call(subMenuContext, e, reason);
            (_b = menuListContext === null || menuListContext === void 0 ? void 0 : menuListContext.onClose) === null || _b === void 0 ? void 0 : _b.call(menuListContext, e, reason);
        },
    }); }, [handleCloseSubMenu, menuListContext, subMenuContext]);
    var modifiers = useMemo(function () { return ({
        flip: {
            enabled: true,
        },
        offset: {
            offset: -8,
        },
        preventOverflow: {
            enabled: true,
            boundariesElement: externalWindow ? 'window' : 'viewport',
        },
    }); }, [externalWindow]);
    return (React.createElement(React.Fragment, null,
        React.createElement(RcMenuItem, __assign({ "aria-controls": popperId }, rest, { ref: ref, disabled: disabled, classes: classes, onKeyDown: handleItemKeyDown, onTouchStart: handleItemTouchStart, onTouchEnd: handleItemTouchEnd, onMouseEnter: handleItemMouseEnter, onMouseLeave: handleItemMouseLeave, idRef: menuItemIdRef, isSubMenuItem: true, "aria-haspopup": "true", "aria-expanded": open }),
            title,
            React.createElement(StyledListItemSecondaryAction, null,
                React.createElement(RcIcon, { size: "medium", color: "neutral.f04", symbol: ArrowRight }))),
        React.createElement(RcPopper, __assign({ role: "menu", id: popperId, container: externalWindow === null || externalWindow === void 0 ? void 0 : externalWindow.document.body, placement: "right-start" }, restPopperProps, { ref: popperRef, open: open, anchorEl: anchorEl, transition: true, modifiers: modifiers, onKeyDown: handlePopperKeyDown, onMouseLeave: handlePopperMouseLeave }), function (_a) {
            var TransitionProps = _a.TransitionProps;
            return (React.createElement(StyledGrow, __assign({}, TransitionProps, { timeout: "auto" }),
                React.createElement(RcPaper, null,
                    React.createElement(RcSubMenuContext.Provider, { value: ctxValue },
                        React.createElement(ClickAwayListener, { onClickAway: handlePopperClickAway },
                            React.createElement(MemoMenuList, __assign({}, MenuListProps, { autoFocusItem: true }), children))))));
        })));
});
var RcSubMenu = styled(_RcSubMenu)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  ", ";\n"], ["\n  ", ";\n"])), SubMenuStyle);
RcSubMenu.defaultProps = {};
RcSubMenu.displayName = 'RcSubMenu';
export { RcSubMenu, RcSubMenuContext };
var templateObject_1;
